<template>
  <two-column-layout>
    <card-container>
      <h2>{{ $t(i18n.title) }}</h2>
      <p>{{ $t(i18n.description) }}</p>

      <text-input-fragment
        :label="$t(i18n.input.name.label)"
        v-model="form.fullName"
        :is-invalid="isFieldInvalid($v.form.fullName)"
        :disabled="profileLoading"
        size="medium"
      >
        {{ $t(i18n.input.name.description) }}
        <template #result>
          <field-error-messages :validation-state="$v.form.fullName" />
        </template>
      </text-input-fragment>

      <text-input-fragment
          :label="$t(i18n.input.phone.label)"
          v-model="form.phoneNumber"
          :is-invalid="isFieldInvalid($v.form.phoneNumber)"
          :disabled="profileLoading"
          size="small"
      >
        {{ $t(i18n.input.phone.description) }}
        <template #result>
          <field-error-messages :validation-state="$v.form.phoneNumber" />
        </template>
      </text-input-fragment>

      <text-input-fragment
          :label="$t(i18n.input.email.label)"
          v-model="form.email"
          :is-invalid="isFieldInvalid($v.form.email)"
          :disabled="profileLoading"
      >
        {{ $t(i18n.input.email.description) }}
        <template #result>
          <field-error-messages :validation-state="$v.form.email" />
        </template>
      </text-input-fragment>

      <fragment-separator></fragment-separator>

      <date-input-fragment
          :label="$t(i18n.input.damage_date.label)"
          v-model="form.damageDate"
          :is-invalid="isFieldInvalid($v.form.damageDate)"
          @input="loadClaimTypes"
          show-info
          @info-clicked="onInfoClicked('DAMAGE_CLAIM_DAMAGE_DATE')"
          size="small"
      >
        {{ $t(i18n.input.damage_date.description) }}
        <template #result>
          <field-error-messages :validation-state="$v.form.damageDate" />
        </template>
      </date-input-fragment>

      <fragment-separator v-if="!!form.damageDate"></fragment-separator>

      <div class="spinner-wrapper" v-if="!!form.damageDate && claimTypesLoading">
        <span class="spinner"></span>
      </div>

      <div v-if="!!form.damageDate && !claimTypesLoading">
        <radio-group-fragment
            v-if="!isObjectEmpty(claimTypes)"
            key="select-damage-type"
            :label="$t(i18n.input.claim_type.label)"
            :description="$t(i18n.description)"
            v-model="form.claimType"
            @input="loadInsuredAssets(null, null, true)"
            :is-invalid="isFieldInvalid($v.form.claimType)"
            group-name="group damage type"
            :options="claimTypes"
            :disabled="claimTypeSelectedAtLeastOnce && insuredAssetsLoading"
            show-info
            @info-clicked="onInfoClicked('DAMAGE_CLAIM_CLAIM_TYPE')"
        >
          {{ $t(i18n.input.claim_type.description) }}
          <template #result>
            <field-error-messages :validation-state="$v.form.claimType" />
          </template>
        </radio-group-fragment>

        <p v-if="isObjectEmpty(claimTypes)">{{ $t(i18n.no_policies_applicable_on_date) }}</p>
      </div>

      <div class="spinner-wrapper" v-if="form.claimType && insuredAssetsLoading && !insuredAssetsLoadingFromSearch">
        <div class="spinner"></div>
      </div>

      <div v-if="form.claimType && (!insuredAssetsLoading || insuredAssetsLoadingFromSearch)">
        <radio-group-fragment
            v-if="!dropDownActive"
            key="select-insured-asset"
            :label="$t(i18n.input[claimType].label)"
            :description="$t(i18n.description)"
            v-model="form.insuredAssetId"
            @input="setInsuredAsset"
            :is-invalid="isFieldInvalid($v.form.insuredAssetId)"
            group-name="group-insured-asset"
            :options="insuredAssetOptions"
            show-info
            @info-clicked="onInfoClicked(damageClaimObjectOrLocationTag)"
        >
          {{ $t(i18n.input[claimType].description) }}
          <template #result>
            <field-error-messages :validation-state="$v.form.insuredAssetId" />
          </template>
        </radio-group-fragment>

        <filterable-drop-down-fragment
            v-if="dropDownActive"
            :label="$t(i18n.input[claimType].label)"
            :options="insuredAssetArray"
            v-model="form.insuredAssetId"
            @input="setInsuredAsset"
            :visibleOptions="visibleOptions"
            :is-invalid="isFieldInvalid($v.form.insuredAssetId)"
            show-info
            @search="loadInsuredAssets"
            @info-clicked="onInfoClicked(damageClaimObjectOrLocationTag)"
            :placeholder="$t(i18n.input[claimType].placeholder)"
            :listFooterLabel="$t(i18n.assset_selection_footer_label)"
        >
          {{ $t(i18n.input[claimType].description) }}
          <template #result>
            <field-error-messages :validation-state="$v.form.insuredAssetId" />
          </template>
        </filterable-drop-down-fragment>

        <fragment-separator></fragment-separator>

        <text-input-fragment
            :label="$t(i18n.input.reference.label)"
            v-model="form.reference"
            :is-invalid="isFieldInvalid($v.form.reference)"
            show-info
            @info-clicked="onInfoClicked('DAMAGE_CLAIM_REFERENCE')"
        >
          {{ $t(i18n.input.reference.description) }}
          <template #result>
            <field-error-messages :validation-state="$v.form.reference" />
          </template>
        </text-input-fragment>
      </div>

      <button-group>
        <button-fragment
            @click="onSubmit()"
            :disabled="isObjectEmpty(claimTypes)"
        >
          {{ $t(i18n.input.submit) }}
        </button-fragment>
      </button-group>
    </card-container>
  </two-column-layout>
</template>

<script>

import {
    ButtonGroup,
    FragmentSeparator,
    ButtonFragment,
    CardContainer,
    TwoColumnLayout,
    TextInputFragment,
    DateInputFragment,
    RadioGroupFragment,
    FilterableDropDownFragment
} from "@bvm/forms";
import { Utils } from "@bvm/core";
import {
  and,
  email,
  maxLength,
  minLength,
  not,
  numeric,
  or,
  required } from "vuelidate/lib/validators";
import { isSaneYear, todayOrBefore } from "../../validations";
import FieldErrorMessages from "../shared/field-error-messages";
import MediaItemModal from "../shared/media-item-modal";
import { getAvailableProducts, getInsuredAssets, getInsuredLocations } from "./api/damage-claim-api";
import { getCurrentProfile, updateCurrentProfile } from "../../api/profile-api";
import { goToSpecialPage } from "@bvm/core/src/api/specialpage/specialpage.api";

const i18nBuilder = new Utils.I18n.I18nBuilder("claim_damage")
    .add("assset_selection_footer_label")
    .add("item_loading_failure")
    .add("no_policies_applicable_on_date")
    .add("claim_types", b => b.add("auto")
        .add("vehicle_with_license_plate")
        .add("vehicle_no_license_plate")
        .add("equipment")
        .add("client_object")
        .add("company_building")
        .add("company_liability")
        .add("company_damage")
        .add("company_legal_assistance")
        .add("company_collections_assistance")
        .add("tank_coverage")
        .add("environmental_contamination")
        .add("machine")
        .add("monetary")
        .add("accident")
        .add("proper_employment")
        .add("bicycle")
    )
    .add("input", b => b.add("submit")
      .add("name", c => c.add("label").add("description"))
      .add("phone", c => c.add("label").add("description"))
      .add("email", c => c.add("label").add("description"))
      .add("reference", c => c.add("label").add("description"))
      .add("damage_date", c => c.add("label").add("description"))
      .add("claim_type", c => c.add("label").add("description"))
      .add("claim_object", c => c.add("label").add("description").add("placeholder"))
      .add("claim_location", c => c.add("label").add("description").add("placeholder")))
    .add("title")
    .add("description");

const objectRelatedClaimTypes = [
    "vehicle_with_license_plate",
    "equipment",
    "bicycle"
];
const radioGroupItemLimitBeforeSwitchingToDropDown = 5;

export const messageKeys = i18nBuilder.getKeys();

export default {
  name: "damage-claim-form",

  components: {
    FragmentSeparator,
    ButtonFragment,
    ButtonGroup,
    CardContainer,
    TwoColumnLayout,
    TextInputFragment,
    DateInputFragment,
    RadioGroupFragment,
    FieldErrorMessages,
    FilterableDropDownFragment
  },

  data() {
    return {
      i18n: i18nBuilder.getData(),
      visibleOptions: 6,
      form: {
        fullName: null,
        email: null,
        phoneNumber: null,
        damageDate: null,
        reference: null,
        claimType: null,
        insuredAssetId: null
      },
      profileLoading: true,
      fetchedPhoneNumber: null,
      fetchedEmailAddress: null,
      claimTypes: {},
      claimTypesLoading: true,
      claimTypeSelectedAtLeastOnce: false,
      insuredAssets: [],
      insuredAssetOptions: {},
      insuredAssetArray: [],
      insuredAssetsLoading: true,
      insuredAssetsLoadingFromSearch: true,
      resultFromSearch: false,
      resetOnOptionsChange: false
    };
  },
  computed: {
    claimType() {
      return this.isClaimForObject ? "claim_object" : "claim_location";
    },
    damageClaimObjectOrLocationTag() {
      return this.isClaimForObject ? 'DAMAGE_CLAIM_OBJECT' : 'DAMAGE_CLAIM_LOCATION';
    },
    dropDownActive() {
      return this.insuredAssetArray.length > radioGroupItemLimitBeforeSwitchingToDropDown || this.resultFromSearch;
    },
    isClaimForLocation() {
      return this.form.claimType && !objectRelatedClaimTypes.includes(this.form.claimType);
    },
    isClaimForObject() {
      return this.form.claimType && objectRelatedClaimTypes.includes(this.form.claimType);
    }
  },
  created() {
    this.loadContactInfo();
  },
  methods: {
    onSubmit() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.scrollFirstErrorIntoView(0);
        return;
      }

      this.updateProfile();

      const asset = this.form.insuredAsset;

      if (asset.product.code === 'fleet') {
        goToSpecialPage("FLEET_DAMAGE_CLAIM",
            {key: 'fullName', value: this.form.fullName},
            {key: 'phoneNumber', value: this.form.phoneNumber},
            {key: 'emailAddress', value: this.form.email},
            {key: 'reference', value: this.form.reference},
            {key: 'licensePlate', value: asset.licensePlate},
            {key: 'damageDate', value: this.form.damageDate}
        );
      } else {
        goToSpecialPage("AXON_WIDGET",
            {key: 'policyExtId', value: this.form.insuredAssetId},
            {key: 'dateDamage', value: this.formatDateForAxon(this.form.damageDate)},
            {key: 'contactpersoonNaam', value: this.form.fullName},
            {key: 'contactpersoonTelNr', value: this.form.phoneNumber},
            {key: 'contactpersoonEmailadres', value: this.form.email},
            {key: 'schadeKlantkenmerk', value: this.form.reference},
            {key: 'soortSchademelding', value: asset.product.code}
        );
      }
    },
    formatDateForAxon(dateString) {
      const dateObj = new Date(dateString);
      // Axon requires dates to be ordered day/month/year, with no requirement for leading 0's
      // Default datepicker output is ordered year/month/day, thus requiring the switcharoo
      return `${dateObj.getDate()}-${dateObj.getMonth() + 1}-${dateObj.getFullYear()}`;
    },
    updateProfile() {
      const emailAddressUpdated = this.form.email !== this.fetchedEmailAddress;
      const phoneNumberUpdated = this.form.phoneNumber !== this.fetchedPhoneNumber;

      if (emailAddressUpdated || phoneNumberUpdated) {
        updateCurrentProfile({
          fullName: this.form.fullName, // Full name will not be permanently overridden, see ProfileDTO comment in UAA Service API
          emailAddress: emailAddressUpdated ? this.form.email : this.fetchedEmailAddress,
          phoneNumber: phoneNumberUpdated ? this.form.phoneNumber : this.fetchedPhoneNumber
        })
      }
    },
    onInfoClicked(mediaItemTag) {
      this.$bvmModal.show(MediaItemModal, {
        itemTag: mediaItemTag
      }, {
        clickToClose: true
      });
    },
    isFieldInvalid(field) {
      return field.$dirty && field.$invalid;
    },
    loadContactInfo() {
      getCurrentProfile().then(profile => {
        this.form.fullName = profile.fullName;
        this.form.phoneNumber = (profile.phoneNumber || '').split(/[\D]/).join('');
        this.form.email = profile.emailAddress;

        this.fetchedPhoneNumber = profile.phoneNumber;
        this.fetchedEmailAddress = profile.emailAddress;

        this.profileLoading = false;
      }).catch(() => {
        this.profileLoading = false;
      })
    },
    setInsuredAsset(selectedValue) {
      this.form.insuredAssetId = selectedValue;

      if (selectedValue) {
        this.form.insuredAsset = this.insuredAssets.find(asset => asset.id === this.form.insuredAssetId);
      } else {
        this.form.insuredAsset = null;
      }
    },
    loadInsuredAssets(searchTerm, loading, claimTypeSwitched) {
      // Handle changing options for dropdown on search event
      // No switching to radio group or resetting the selection if we search
      this.resultFromSearch = searchTerm;
      this.resetOnOptionsChange = !!claimTypeSwitched;
      this.claimTypeSelectedAtLeastOnce = this.claimTypeSelectedAtLeastOnce || claimTypeSwitched;

      this.setInsuredAsset(claimTypeSwitched ? null : this.form.insuredAssetId);
      this.insuredAssetsLoading = true;
      this.insuredAssetsLoadingFromSearch = !claimTypeSwitched;

      if(loading) {
        loading(true);
      }

      if (this.isClaimForObject) {
        getInsuredAssets(this.form.claimType, searchTerm, this.form.damageDate).then(insuredAssets => {
          this.parseInsuredAssets(insuredAssets, loading);
        }).catch(() => this.notifyLoadingFailure());
      } else {
        getInsuredLocations(this.form.claimType, searchTerm, this.form.damageDate).then(insuredAssets => {
          this.parseInsuredAssets(insuredAssets, loading);
        }).catch(() => this.notifyLoadingFailure());
      }
    },
    parseInsuredAssets(insuredAssets, loading) {
      this.insuredAssets = insuredAssets.content || [];
      this.insuredAssetArray = [];
      this.insuredAssetOptions = {};

      // Parsing into multiple data formats: Our radio group requires { firstKey: 'firstLabel, .. } input,
      // while our filterable dropdown requires [{value: 'val', label: 'label'}, ..] input due to wrapping
      // vue-select.
      for (const insuredAsset of this.insuredAssets) {
        const label = this.getInsuredAssetLabel(insuredAsset);
        this.insuredAssetArray.push({value: insuredAsset.id, label: label});
        this.insuredAssetOptions[insuredAsset.id] = label;
      }

      if (this.insuredAssetArray.length === 1) {
        this.setInsuredAsset(this.insuredAssetArray[0].value);
      }

      this.insuredAssetsLoading = false;
      this.insuredAssetsLoadingFromSearch = false;

      if (loading) {
        loading(false);
      }
    },
    getInsuredAssetLabel(insuredAsset) {
      let identifier = insuredAsset.vinNumber;
      if (insuredAsset.product.code !== 'fietsVerzekering') {
          identifier = Utils.LicensePlate.format(insuredAsset.licensePlate);
      }

      return this.isClaimForObject ?
          `${identifier || 'Onbekend'} | ${insuredAsset.brandModel || ''}` :
          `${insuredAsset.locationAddress.street} ${insuredAsset.locationAddress.houseNumber}${insuredAsset.locationAddress.houseNumberAddition || ''} ${insuredAsset.locationAddress.postalCode} ${insuredAsset.locationAddress.city}`;
    },
    loadClaimTypes(insertedValue) {
      if (!isSaneYear(insertedValue)) {
        return;
      }

      this.claimTypesLoading = true;
      this.claimTypes = {};
      this.form.claimType = null;
      this.setInsuredAsset(null);

      // Available product groups are the types of claims we can process
      getAvailableProducts(this.form.damageDate).then(result => {
        if (result === undefined) {
          return;
        }
        for (const claimType of result) {
          this.claimTypes[claimType] = this.$t(this.i18n.claim_types[claimType]);
        }
      }).catch(() => {
        Bovemij.notifyFailure('',this.$t(this.i18n.item_loading_failure));
      }).finally(() => {
        this.claimTypesLoading = false;
      });
    },
    isObjectEmpty(obj) {
      return obj && Object.keys(obj).length === 0;
    },
    notifyLoadingFailure() {
      this.insuredAssetsLoading = false;
      this.insuredAssetsLoadingFromSearch = false;
      this.insuredAssets = [];
      this.insuredAssetArray = [];
      this.insuredAssetOptions = {};
      Bovemij.notifyFailure('',this.$t(this.i18n.item_loading_failure))
    },
    scrollFirstErrorIntoView(triedCount = 0) {
      // Give up retrying for errors after 10 retry attempts
      if (triedCount > 10) {
        return;
      }

      let errors = window.document.querySelectorAll('[class^="field-error-message"]');

      // Errors take time to appear, retry if none are found
      if (!errors.length) {
        setTimeout(() => this.scrollFirstErrorIntoView(triedCount + 1), 100);
        return;
      }

      errors[0].scrollIntoView(true); // Scroll to first error
      window.scrollBy(0, -200); // Make sure related field is in view
    }
  },
  validations() {
    return {
      form: {
        fullName: {required},
        email: {
          required,
          email
        },
        phoneNumber: {
          required,
          phone_number: and(or(minLength(10), not(required)), maxLength(10), numeric)
        },
        damageDate: {
          required,
          todayOrBefore,
          isSaneYear
        },
        reference: {required},
        claimType: {required},
        insuredAssetId: {required}
      }
    };
  }
};
</script>